import { Link } from 'react-router-dom';
import { Section } from '../common/Section';
import { Eyebrow } from '../common/Eyebrow';
import { SplitHeading } from '../animation/SplitHeading';
import { Reveal } from '../animation/Reveal';
import { Icon } from '../common/Icon';
import { primaryImage } from '../../lib/image';
import { accentAlpha } from '../../lib/slug';
import { cn } from '../../lib/cn';
import { useCategories, useProducts } from '../../hooks';
import type { Category, Product } from '../../types';

function ProductCount({ n, className }: { n: number; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-sm font-semibold text-ink-700 transition-colors group-hover:text-brand-600', className)}>
      {n} {n === 1 ? 'product' : 'products'}
      <Icon name="ArrowUpRight" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </span>
  );
}

/** Large 2×2 anchor tile — filled with a real product-family showcase so there's no empty space. */
function FeatureTile({ cat, items }: { cat: Category; items: Product[] }) {
  return (
    <Link
      to={`/product-categories/${cat.slug}`}
      className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-sand-200 p-8 shadow-soft transition-shadow duration-500 hover:shadow-lift sm:col-span-2 lg:row-span-2"
      style={{ background: `linear-gradient(160deg, ${accentAlpha(cat.accentColor, 0.2)}, #ffffff 70%)` }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full opacity-70 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: accentAlpha(cat.accentColor, 0.4) }}
      />
      <div className="relative flex flex-1 flex-col gap-8 sm:flex-row sm:items-center lg:flex-col lg:items-stretch">
        <div className="sm:flex-1 lg:flex-none">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-soft" style={{ background: cat.accentColor }}>
            <Icon name={cat.icon} className="h-7 w-7" />
          </span>
          <h3 className="mt-6 font-display text-3xl font-semibold text-ink">{cat.name}</h3>
          <p className="mt-3 max-w-md leading-relaxed text-ink-600">{cat.description}</p>
        </div>

        {items.length > 0 && (
          <div className="flex items-stretch gap-2.5 sm:flex-1 sm:gap-3 lg:mt-2 lg:flex-none">
            {items.map((p, idx) => {
              const img = primaryImage(p);
              return (
                <span
                  key={p.id}
                  className="flex flex-1 items-center justify-center rounded-2xl bg-white p-2 shadow-soft ring-1 ring-black/[0.04] transition-transform duration-500 group-hover:-translate-y-1.5"
                  style={{ transitionDelay: `${idx * 50}ms` }}
                  title={p.name}
                >
                  {img ? (
                    <img src={img} alt={p.name} loading="lazy" className="h-24 w-full object-contain sm:h-32" />
                  ) : (
                    <Icon name="Droplets" className="h-8 w-8 text-ink-300" />
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="relative mt-6 flex items-center justify-between border-t border-sand-200/70 pt-4">
        <span className="text-sm text-ink-500">{cat.tagline}</span>
        <ProductCount n={items.length} />
      </div>
    </Link>
  );
}

function CategoryTile({ cat, items }: { cat: Category; items: Product[] }) {
  return (
    <Link
      to={`/product-categories/${cat.slug}`}
      className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-sand-200 p-6 shadow-soft transition-shadow duration-500 hover:shadow-lift"
      style={{ background: `linear-gradient(155deg, ${accentAlpha(cat.accentColor, 0.16)}, #ffffff 72%)` }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full opacity-60 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: accentAlpha(cat.accentColor, 0.35) }}
      />
      <div className="relative">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-soft" style={{ background: cat.accentColor }}>
          <Icon name={cat.icon} className="h-5 w-5" />
        </span>
        <h3 className="mt-4 font-display text-xl font-semibold text-ink">{cat.name}</h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-ink-500">{cat.description}</p>
      </div>

      <div className="relative mt-5 flex items-center justify-between gap-2">
        <div className="flex -space-x-2.5">
          {items.slice(0, 3).map((p) => {
            const img = primaryImage(p);
            return (
              <span key={p.id} className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-sand-200 bg-sand-100 shadow-sm" title={p.name}>
                {img ? <img src={img} alt="" className="h-full w-full object-contain p-0.5" loading="lazy" /> : <Icon name="Droplets" className="h-4 w-4 text-ink-300" />}
              </span>
            );
          })}
        </div>
        <ProductCount n={items.length} />
      </div>
    </Link>
  );
}

export function CategoryShowcase() {
  const { data: categories = [] } = useCategories();
  const { data: all = [] } = useProducts();
  const itemsFor = (slug: string) => all.filter((p) => p.categorySlug === slug);

  return (
    <Section className="section bg-canvas" containerClassName="">
      <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Eyebrow>Explore the range</Eyebrow>
          <SplitHeading as="h2" text="Five ways we bring soil and crops to life." className="mt-4 max-w-2xl font-display text-h1 font-semibold text-ink" />
        </div>
        <Reveal y={16}>
          <Link to="/catalog" className="link-underline inline-flex items-center gap-2 font-semibold text-brand-600">
            All products <Icon name="ArrowRight" className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>

      <Reveal className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" stagger={0.08} y={30} fade={false}>
        {categories.map((cat, i) =>
          i === 0 ? (
            <FeatureTile key={cat.slug} cat={cat} items={itemsFor(cat.slug)} />
          ) : (
            <CategoryTile key={cat.slug} cat={cat} items={itemsFor(cat.slug)} />
          ),
        )}
      </Reveal>
    </Section>
  );
}
